import type { QueryClient } from '@tanstack/react-query';

import { EQUIPMENT_LIBRARY_RULES, atPlant } from '../../../access';
import { parseSearch } from '../../../navigation/searchParams';
import type { WarmUp, WarmUpCan } from '../../../routes/warmRoutes';
import type { RegistryListFilters } from '../utils/query-keys';
import {
  libraryListQueryOptions,
  libraryTypeQueryOptions,
  nodeTypesQueryOptions,
  plantTypesQueryOptions,
  readingsQueryOptions,
  registryEntryQueryOptions,
  registryListQueryOptions,
  typeHistoryQueryOptions,
} from '../utils/queryOptions';
import { machineSearchSchema } from '../utils/searchSchemas';

const LIVE_MACHINES: RegistryListFilters = { includeArchived: false };

const canSeeLibrary = (can: WarmUpCan): boolean => can(EQUIPMENT_LIBRARY_RULES.view);

const canSeePlant = (can: WarmUpCan, plantId: string): boolean => can(EQUIPMENT_LIBRARY_RULES.plant, atPlant(plantId));

const machineFiltersOf = (searchParams: URLSearchParams): RegistryListFilters => ({
  includeArchived: parseSearch(machineSearchSchema, searchParams).archived,
});

const warmTypeNames = (queryClient: QueryClient, can: WarmUpCan): void => {
  if (!canSeeLibrary(can)) return;
  void queryClient.prefetchQuery(nodeTypesQueryOptions(true));
  void queryClient.prefetchQuery(libraryListQueryOptions(true));
};

const warmMachineList = (queryClient: QueryClient, can: WarmUpCan, plantId: string | undefined, filters: RegistryListFilters): void => {
  if (plantId === undefined || !canSeePlant(can, plantId)) return;
  void queryClient.prefetchQuery(registryListQueryOptions(plantId, filters, true));
};

export const warmLibraryTypes: WarmUp = ({ queryClient, can }) => {
  if (!canSeeLibrary(can)) return;
  void queryClient.prefetchQuery(libraryListQueryOptions(true));
};

export const warmPlantTypes: WarmUp = ({ queryClient, can }) => {
  if (!canSeeLibrary(can)) return;
  void queryClient.prefetchQuery(plantTypesQueryOptions(true));
  void queryClient.prefetchQuery(nodeTypesQueryOptions(true));
};

export const warmScadaVocabulary: WarmUp = ({ queryClient, can }) => {
  if (!canSeeLibrary(can)) return;
  void queryClient.prefetchQuery(readingsQueryOptions(true));
  void queryClient.prefetchQuery(nodeTypesQueryOptions(true));
};

export const warmLibraryType: WarmUp = ({ queryClient, params, can }) => {
  if (params.code === undefined || !canSeeLibrary(can)) return;
  void queryClient.prefetchQuery(libraryTypeQueryOptions(params.code, true));
  void queryClient.prefetchQuery(typeHistoryQueryOptions(params.code, true));
};

export const warmTypeEditor: WarmUp = ({ queryClient, params, can }) => {
  if (!canSeeLibrary(can)) return;
  void queryClient.prefetchQuery(readingsQueryOptions(true));
  if (params.code !== undefined) void queryClient.prefetchQuery(libraryTypeQueryOptions(params.code, true));
};

export const warmMachines: WarmUp = ({ queryClient, params, searchParams, can }) => {
  warmTypeNames(queryClient, can);
  warmMachineList(queryClient, can, params.plantId, machineFiltersOf(searchParams));
};

export const warmMachine: WarmUp = ({ queryClient, params, searchParams, can }) => {
  const { plantId, entryId } = params;
  warmTypeNames(queryClient, can);
  warmMachineList(queryClient, can, plantId, machineFiltersOf(searchParams));
  if (plantId === undefined || entryId === undefined || !canSeePlant(can, plantId)) return;
  void queryClient.prefetchQuery(registryEntryQueryOptions(plantId, entryId, true));
};

export const warmScadaSync: WarmUp = ({ queryClient, params, can }) => {
  warmMachineList(queryClient, can, params.plantId, LIVE_MACHINES);
};
